import { useCallback, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { useAuthStore } from '../stores/auth.store.ts';
import { useCrmStore } from '../stores/crm.store.ts';
import { useSettingsStore } from '../stores/settings.store.ts';
import * as api from '../lib/api.ts';
import { getDaysAbsent, getDaysUntil } from '../utils/date.ts';
import { getCurrencySymbol } from '../utils/currency.ts';
import { matchesCrmSubFilter } from '../utils/crmHelpers.ts';
import {
  extractRelationalBookingEntities,
  extractRelationalSaleItemEntities,
} from '../utils/relationalResolution.ts';
import {
  getCrmClients,
  saveCrmClients,
  getAllCustomerStats,
  saveCustomerStatsBatch,
  getAppMeta,
  setAppMeta,
} from '../lib/db/index.ts';
import { runDeltaSync } from '../utils/deltaSyncEngine.ts';
import { resolveLocationName } from '../utils/locationResolution.ts';
import type {
  CrmClient,
  CrmSegment,
  CrmGroupBy,
  CrmSubFilter,
  CustomerStatsCacheItem,
} from '../types/crm.ts';
import type { Settings } from '../types/settings.ts';

const STATS_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days TTL

export function useCrm() {
  const { workspaceId } = useAuthStore();
  const settings = useSettingsStore();
  const {
    clients,
    segments,
    activeSegmentId,
    activeSubFilter,
    segmentMemberships,
    loading,
    loadingPhase,
    loadedCount,
    totalCount,
    searchQuery,
    sortBy,
    sortOrder,
    groupBy,
    hideProcessed,
    statsProgress,
    setClients,
    updateClientsBatch,
    setSegments,
    setActiveSegmentId,
    setActiveSubFilter,
    setSegmentMemberships,
    setLoading,
    setLoadingProgress,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setGroupBy,
    setHideProcessed,
    markProcessed,
    unmarkProcessed,
    isProcessed,
    updateStatsCache,
    setStatsCacheBatch,
    setStatsProgress,
  } = useCrmStore();

  const isFetchingRef = useRef(false);
  const isFetchingSegmentsRef = useRef(false);
  const currentSessionIdRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup in-flight stats queues on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // 1. Initial 0ms Snapshot Loading from Dexie (Segments, Memberships & Clients)
  useEffect(() => {
    if (!workspaceId) return;

    // Load cached segment definitions if store is empty
    if (useCrmStore.getState().segments.length === 0) {
      getAppMeta<CrmSegment[]>('crm_segments')
        .then((cachedSegs) => {
          if (cachedSegs && cachedSegs.length > 0 && useCrmStore.getState().segments.length === 0) {
            setSegments(cachedSegs);
          }
        })
        .catch((err) => {
          console.warn('Failed to load cached CRM segments from Dexie:', err);
        });
    }

    // Load cached segment memberships, clients and stats from Dexie for immediate 0ms render
    getAppMeta<Record<string, string[]>>('crm_segment_memberships')
      .then(async (cachedMemberships) => {
        if (
          cachedMemberships &&
          Object.keys(cachedMemberships).length > 0 &&
          Object.keys(useCrmStore.getState().segmentMemberships).length === 0
        ) {
          setSegmentMemberships(cachedMemberships);
        }

        const currentMemberships = useCrmStore.getState().segmentMemberships;
        const memberships =
          cachedMemberships && Object.keys(cachedMemberships).length > 0
            ? cachedMemberships
            : currentMemberships;

        const [cachedClients, cachedStats] = await Promise.all([
          getCrmClients(workspaceId, activeSegmentId, memberships),
          getAllCustomerStats(),
        ]);

        if (cachedClients.length > 0 && useCrmStore.getState().clients.length === 0) {
          setClients(cachedClients);
        }
        if (Object.keys(cachedStats).length > 0) {
          setStatsCacheBatch(cachedStats);
        }

        // Run background delta sync to patch any recent appointment/sales changes
        if (workspaceId) {
          runDeltaSync(workspaceId).catch((err) => {
            console.warn('Background delta sync failed (non-critical):', err);
          });
        }
      })
      .catch((cacheErr) => {
        console.warn('Failed to read Dexie snapshot in useCrm:', cacheErr);
      });
  }, [workspaceId, activeSegmentId, setSegments, setSegmentMemberships, setClients, setStatsCacheBatch]);

  // 3. Fetch Native Segment Memberships in Background (100% agnostic for custom & default segments)
  const fetchSegmentMemberships = useCallback(
    async (force = false, explicitSegments?: CrmSegment[]) => {
      if (!workspaceId) return;
      if (isFetchingSegmentsRef.current && !force) return;
      isFetchingSegmentsRef.current = true;

      try {
        const currentSegs =
          explicitSegments || useCrmStore.getState().segments;
        const targetSegments = currentSegs.map((s) => String(s.id)).filter(Boolean);
        const newMemberships: Record<string, string[]> = {};

        const CONCURRENCY = 2;
        for (let i = 0; i < targetSegments.length; i += CONCURRENCY) {
          const chunk = targetSegments.slice(i, i + CONCURRENCY);
          await Promise.allSettled(
            chunk.map(async (segId) => {
              const customerIds: string[] = [];
              let offset = 0;
              const limit = 50;
              let hasMore = true;
              let fetchFailed = false;

              while (hasMore) {
                try {
                  const res = await api.searchCustomers({
                    workspaceId,
                    limit,
                    offset,
                    segmentId: segId || undefined,
                  });

                  const data = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
                  for (const item of data) {
                    const id = String(item?.id || '');
                    if (id) customerIds.push(id);
                  }

                  if (data.length === limit && customerIds.length < 2000) {
                    offset += limit;
                  } else {
                    hasMore = false;
                  }
                } catch (err) {
                  console.warn(`Failed to fetch membership for segment ${segId}:`, err);
                  hasMore = false;
                  fetchFailed = true;
                }
              }

              // Fix NET-03: ONLY record new membership if the fetch succeeded!
              // Never overwrite existing memberships with empty arrays on network/502 failure!
              if (!fetchFailed) {
                newMemberships[segId] = customerIds;
              }
            })
          );

          if (i + CONCURRENCY < targetSegments.length) {
            await new Promise((r) => setTimeout(r, 60)); // Short pause between chunks to protect gateway
          }
        }

        // Fix NET-03 (Non-destructive merge with existing memberships)
        if (Object.keys(newMemberships).length > 0) {
          const currentMemberships = useCrmStore.getState().segmentMemberships;
          const mergedMemberships = {
            ...currentMemberships,
            ...newMemberships,
          };
          setSegmentMemberships(mergedMemberships);
          await setAppMeta('crm_segment_memberships', mergedMemberships).catch(console.warn);
        }
      } catch (err) {
        console.error('Failed to fetch CRM segment memberships:', err);
      } finally {
        isFetchingSegmentsRef.current = false;
      }
    },
    [workspaceId, setSegmentMemberships]
  );

  // 2. Fetch Native Segment Definitions from Fresha API
  useEffect(() => {
    if (!workspaceId) return;

    api
      .getSegments(workspaceId)
      .then((res) => {
        const edges =
          res?.data?.segmentDefinitions?.edges ||
          res?.data?.data?.segmentDefinitions?.edges ||
          [];
        const segs: CrmSegment[] = edges
          .map((e: any) => ({
            id: String(e.node?.id || ''),
            name: e.node?.name || '',
            description: e.node?.description || '',
            predefinedType: e.node?.predefinedType || undefined,
            predefinedSettings: e.node?.predefinedSettings || undefined,
            rules: e.node?.rules || [],
            customerCount: e.node?.size?.customerCount ?? undefined,
            count: e.node?.size?.customerCount ?? undefined,
          }))
          .filter((s: CrmSegment) => Boolean(s.id));

        if (segs.length > 0) {
          setSegments(segs);
          setAppMeta('crm_segments', segs).catch(console.warn);
          fetchSegmentMemberships(true, segs);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch CRM segments:', err);
      });
  }, [workspaceId, setSegments]);

  // Trigger background segment membership fetch when workspaceId or segments are available
  useEffect(() => {
    if (workspaceId && segments.length > 0) {
      fetchSegmentMemberships(false);
    }
  }, [workspaceId, segments.length, fetchSegmentMemberships]);

  // 3. Fetch Customers for Active Segment with Pagination & Enqueue
  const fetchSegmentCustomers = useCallback(
    async (_force = false) => {
      if (!workspaceId) return;

      currentSessionIdRef.current += 1;
      const sessionId = currentSessionIdRef.current;
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      isFetchingRef.current = true;
      setLoading(true);
      setLoadingProgress('customers', 0, 0);

      if (_force && workspaceId) {
        runDeltaSync(workspaceId).catch((err) => {
          console.warn('Force delta sync failed:', err);
        });
      }

      try {
        const allNodes: any[] = [];
        const limit = 50;
        let offset = 0;
        let hasMore = true;

        while (hasMore) {
          if (signal.aborted || currentSessionIdRef.current !== sessionId) return;

          const res = await api.searchCustomers({
            workspaceId,
            limit,
            offset,
            segmentId: activeSegmentId || undefined,
          });

          if (signal.aborted || currentSessionIdRef.current !== sessionId) return;

          const data = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
          const meta = (res as any)?.meta || {};
          const total =
            meta['results-count'] || meta['customers-count'] || data.length;

          if (data.length > 0) {
            allNodes.push(...data);
            setLoadingProgress('customers', allNodes.length, total || allNodes.length);
          }

          if (data.length === limit && allNodes.length < 2000) {
            offset += limit;
          } else {
            hasMore = false;
          }
        }

        if (signal.aborted || currentSessionIdRef.current !== sessionId) return;

        // Map raw API nodes to CrmClient domain model
        const currentCache = useCrmStore.getState().statsCache;
        const currentClients = useCrmStore.getState().clients;
        const existingClientsMap = new Map<string, CrmClient>();
        for (const cl of currentClients) {
          existingClientsMap.set(cl.id, cl);
          if (cl.customerId) existingClientsMap.set(cl.customerId, cl);
        }

        const now = Date.now();
        const statsQueueIds: string[] = [];

        const mapped: CrmClient[] = allNodes.map((c: any, idx: number) => {
          const cid = String(c.id || `crm-${idx}`);
          const existing = existingClientsMap.get(cid);
          const attrs = c.attributes || {};
          const name =
            attrs.name ||
            `${attrs['first-name'] || ''} ${attrs['last-name'] || ''}`.trim() ||
            existing?.name ||
            `Cliente #${idx + 1}`;
          const phone = attrs['contact-number'] || attrs.telephone || existing?.phone || '';
          const email = attrs.email || existing?.email || '';
          let sales = Number(attrs['total-sales-amount'] ?? existing?.totalSales ?? 0);
          let debt = Number(
            attrs['outstanding-amount'] ??
              attrs['outstanding-sales-amount'] ??
              existing?.outstandingAmount ??
              0
          );

          let visits = existing?.totalAppointments || 0;
          let lastAppt = existing?.lastAppointmentDate || '';
          let staff = existing?.lastStaffMember || '';
          let daysAbsent = existing?.daysAbsent || 0;
          let lastLocId = existing?.lastLocationId || '';
          let lastServiceName = existing?.lastServiceName || '';
          let lastLocationName = existing?.lastLocationName || '';
          let nextAppt = existing?.nextAppointmentDate || '';
          let nextStaff = existing?.nextStaffMember || '';
          let nextServiceName = existing?.nextServiceName || '';
          let nextLocationName = existing?.nextLocationName || '';
          let nextLocId = existing?.nextLocationId || '';
          let daysUntilNext: number | undefined = existing?.daysUntilNext;

          const cached = currentCache[cid];
          const salesChanged = existing && Math.abs(sales - (existing.totalSales || 0)) > 0.01;
          const debtChanged = existing && Math.abs(debt - (existing.outstandingAmount || 0)) > 0.01;
          const isFresh =
            !_force &&
            !salesChanged &&
            !debtChanged &&
            cached &&
            typeof cached === 'object' &&
            now - (cached.lastFetch || 0) < STATS_CACHE_TTL_MS;

          if (isFresh) {
            visits = cached.appointments || 0;
            lastAppt = cached.lastAppointment || '';
            staff = cached.staff || '';
            lastServiceName = cached.serviceName || '';
            lastLocationName = cached.locationName || '';
            daysAbsent = lastAppt ? getDaysAbsent(lastAppt) : (cached.daysAbsent ?? 0);
            nextAppt = cached.nextAppointment || '';
            nextStaff = cached.nextStaff || '';
            nextServiceName = cached.nextServiceName || '';
            nextLocationName = cached.nextLocationName || '';
            nextLocId = cached.nextLocationId || '';
            daysUntilNext = nextAppt ? getDaysUntil(nextAppt) : cached.daysUntilNext;
            if (cached.outstandingSales !== undefined && !isNaN(cached.outstandingSales)) {
              debt = cached.outstandingSales;
            }
            if (cached.totalSales !== undefined && !isNaN(cached.totalSales)) {
              sales = cached.totalSales;
            }
          } else {
            statsQueueIds.push(cid);
          }

          return {
            id: cid,
            customerId: cid,
            workspaceId,
            name,
            firstName: attrs['first-name'] ?? existing?.firstName,
            lastName: attrs['last-name'] ?? existing?.lastName,
            phone,
            email,
            birthdayDay: attrs['birthday-day'] ?? existing?.birthdayDay ?? null,
            birthdayMonth: attrs['birthday-month'] ?? existing?.birthdayMonth ?? null,
            createdAt: attrs['created-at'] ?? existing?.createdAt,
            totalSales: sales,
            outstandingAmount: debt,
            totalAppointments: visits,
            lastAppointmentDate: lastAppt || undefined,
            lastStaffMember: staff || undefined,
            lastLocationId: lastLocId || undefined,
            lastLocationName: lastLocationName || undefined,
            lastServiceName: lastServiceName || undefined,
            nextAppointmentDate: nextAppt || undefined,
            nextStaffMember: nextStaff || undefined,
            nextServiceName: nextServiceName || undefined,
            nextLocationName: nextLocationName || undefined,
            nextLocationId: nextLocId || undefined,
            daysUntilNext,
            daysAbsent,
            segmentId: activeSegmentId || existing?.segmentId || undefined,
          };
        });

        setClients(mapped);
        if (activeSegmentId) {
          const currentMemberships = useCrmStore.getState().segmentMemberships;
          const updatedMemberships = {
            ...currentMemberships,
            [activeSegmentId]: mapped.map((c) => c.id),
          };
          setSegmentMemberships(updatedMemberships);
          setAppMeta('crm_segment_memberships', updatedMemberships).catch(console.warn);
        }
        saveCrmClients(mapped).catch((err) => {
          console.warn('Failed to save CRM clients to Dexie:', err);
        });
        setLoadingProgress('idle', mapped.length, mapped.length);

        // Trigger background stats enrichment queue
        if (statsQueueIds.length > 0) {
          processCrmStatsQueue(
            statsQueueIds,
            workspaceId,
            null,
            sessionId,
            signal,
            () => currentSessionIdRef.current,
            updateStatsCache,
            updateClientsBatch,
            setStatsProgress
          );
        }
      } catch (err) {
        console.error('Failed to fetch CRM customers:', err);
      } finally {
        if (currentSessionIdRef.current === sessionId) {
          setLoading(false);
          setLoadingProgress('idle', 0, 0);
          isFetchingRef.current = false;
        }
      }
    },
    [
      workspaceId,
      activeSegmentId,
      setClients,
      updateClientsBatch,
      setLoading,
      setLoadingProgress,
      updateStatsCache,
      setStatsProgress,
    ]
  );

  // Trigger fetch when activeSegmentId or workspaceId changes
  useEffect(() => {
    fetchSegmentCustomers(false);
  }, [fetchSegmentCustomers]);

  // Filtering by search query, subfilter & Inbox Zero toggle
  const filtered = useMemo(() => {
    return clients.filter((c) => {
      if (hideProcessed && isProcessed(c.id)) return false;
      if (activeSubFilter && !matchesCrmSubFilter(c, activeSubFilter, settings, segmentMemberships, segments)) return false;
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase().trim();
      return (
        c.name.toLowerCase().includes(q) ||
        Boolean(c.phone && c.phone.toLowerCase().includes(q)) ||
        Boolean(c.email && c.email.toLowerCase().includes(q)) ||
        Boolean(c.lastStaffMember && c.lastStaffMember.toLowerCase().includes(q))
      );
    });
  }, [clients, hideProcessed, isProcessed, activeSubFilter, settings, searchQuery, segmentMemberships, segments]);

  // Sorting
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'totalSales':
          cmp = (a.totalSales || 0) - (b.totalSales || 0);
          break;
        case 'totalAppointments':
          cmp = (a.totalAppointments || 0) - (b.totalAppointments || 0);
          break;
        case 'daysAbsent':
          cmp = (a.daysAbsent || 0) - (b.daysAbsent || 0);
          break;
        case 'outstandingAmount':
          cmp = (a.outstandingAmount || 0) - (b.outstandingAmount || 0);
          break;
        case 'name':
        default:
          cmp = a.name.localeCompare(b.name);
      }
      return sortOrder === 'desc' ? -cmp : cmp;
    });
  }, [filtered, sortBy, sortOrder]);

  // Grouping
  const grouped = useMemo(() => {
    return groupCrmClients(sorted, groupBy, settings);
  }, [sorted, groupBy, settings]);

  // Dynamic Real-Time SubFilter Counts (Pending / Total) calculated on current active clients
  const subFilterCounts: Record<CrmSubFilter, { pending: number; total: number }> = useMemo(() => {
    const subFilters: CrmSubFilter[] = [
      'all',
      'vip',
      'birthday',
      'top_spender',
      'new',
      'lapsed',
      'debt',
      'with_appointments',
    ];

    const result = {} as Record<CrmSubFilter, { pending: number; total: number }>;

    for (const sf of subFilters) {
      let total = 0;
      let pending = 0;

      for (const client of clients) {
        if (matchesCrmSubFilter(client, sf, settings, segmentMemberships, segments)) {
          total++;
          if (!isProcessed(client.id)) {
            pending++;
          }
        }
      }

      result[sf] = { pending, total };
    }

    return result;
  }, [clients, settings, isProcessed, segmentMemberships, segments]);

  // Dynamic Real-Time Segment Counts (Pending / Total) for Global Dropdown (100% Agnostic)
  const segmentCounts: Record<string, { pending: number; total: number }> = useMemo(() => {
    const allIds = new Set<string>(['', ...segments.map((s) => String(s.id)).filter(Boolean)]);
    Object.keys(segmentMemberships).forEach((id) => allIds.add(id));

    const result: Record<string, { pending: number; total: number }> = {};

    for (const segId of allIds) {
      if (!segId) {
        // Directorio Completo (Todos los clientes)
        const ids = segmentMemberships[''];
        if (ids && ids.length > 0) {
          const total = ids.length;
          const pending = ids.filter((id) => !isProcessed(id)).length;
          result[''] = { pending, total };
        } else {
          const total = totalCount || clients.length || 0;
          const pending = activeSegmentId === '' ? clients.filter((c) => !isProcessed(c.id)).length : total;
          result[''] = { pending, total };
        }
      } else {
        const seg = segments.find((s) => String(s.id) === segId);
        const ids = segmentMemberships[segId];
        if (ids !== undefined) {
          const total = ids.length;
          const pending = ids.filter((id) => !isProcessed(id)).length;
          result[segId] = { pending, total };
        } else {
          const segClients = activeSegmentId === segId ? clients : [];
          const total = seg?.customerCount ?? segClients.length ?? 0;
          const pending = segClients.filter((c) => !isProcessed(c.id)).length;
          result[segId] = { pending, total };
        }
      }
    }

    return result;
  }, [segmentMemberships, isProcessed, segments, activeSegmentId, clients, totalCount]);

  return {
    clients: sorted,
    groupedClients: grouped,
    segments,
    activeSegmentId,
    activeSubFilter,
    segmentCounts,
    subFilterCounts,
    segmentMemberships,
    loading,
    loadingPhase,
    loadedCount,
    totalCount,
    searchQuery,
    sortBy,
    sortOrder,
    groupBy,
    hideProcessed,
    statsProgress,
    fetchSegmentCustomers: () => fetchSegmentCustomers(true),
    fetchSegmentMemberships: () => fetchSegmentMemberships(true),
    setActiveSegmentId,
    setActiveSubFilter,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setGroupBy,
    setHideProcessed,
    markProcessed,
    unmarkProcessed,
    isProcessed,
  };
}

export async function processCrmStatsQueue(
  queueIds: string[],
  workspaceId: string,
  _locationId: string | null = null,
  sessionId: number,
  signal: AbortSignal,
  getCurrentSessionId: () => number,
  _onStatsUpdate: (id: string, item: CustomerStatsCacheItem) => void,
  onBatchUpdate: (updates: Record<string, Partial<CrmClient>>) => void,
  onProgress: (current: number, total: number) => void
) {
  const batchSize = 8;
  const total = queueIds.length;
  let current = 0;
  for (let i = 0; i < queueIds.length; i += batchSize) {
    if (signal.aborted || getCurrentSessionId() !== sessionId) return;

    const batch = queueIds.slice(i, i + batchSize);
    const statsBatch: (CustomerStatsCacheItem & { customerId: string })[] = [];
    const statsBatchMap: Record<string, CustomerStatsCacheItem> = {};
    const chunkUpdates: Record<string, Partial<CrmClient>> = {};

    await Promise.allSettled(
      batch.map(async (cid) => {
        if (signal.aborted || getCurrentSessionId() !== sessionId) return;

        try {
          const [statsRes, apptsRes] = await Promise.all([
            api.getCustomerStats(cid, workspaceId).catch(() => null),
            api
              .getCustomerAppointments(cid, workspaceId, '')
              .catch(() => null),
          ]);

          if (signal.aborted || getCurrentSessionId() !== sessionId) return;

          if (!statsRes && !apptsRes) {
            return; // Do NOT commit zeroed-out stats to cache or chunkUpdates on network failure!
          }

          const stats =
            statsRes?.data?.data?.attributes || statsRes?.data?.attributes || {};
          const count = Number(
            stats['completed-bookings-count'] ?? stats.completedBookingsCount ?? 0
          );
          const outstandingSales = Number(
            stats['outstanding-sales'] ?? stats.outstandingSales ?? 0
          );
          const totalSales = Number(
            stats['total-sales'] ?? stats.totalSales ?? 0
          );

          const now = dayjs().startOf('day');

          const locations = useAuthStore.getState().locations || [];
          const settings = useSettingsStore.getState();

          let nextApptDate = '';
          let nextStaff = '';
          let nextService = '';
          let nextLocation = '';
          let nextLocId = '';
          let daysUntilNext: number | undefined = undefined;

          let lastApptDate = '';
          let lastStaff = '';
          let lastService = '';
          let lastLocation = '';
          let lastLocId = '';
          let daysAbsent: number | undefined = undefined;

          const apptsData = apptsRes?.data?.data || apptsRes?.data || [];
          if (Array.isArray(apptsData)) {
            for (const appt of apptsData) {
              const attr = appt?.attributes || {};
              const firstBooking = attr.bookings?.[0] || {};
              const dateStr =
                firstBooking['start-wall']?.date ||
                attr['start-wall']?.date ||
                attr['start-date'] ||
                '';
              if (!dateStr) continue;

              const staff =
                firstBooking.employee?.name || attr.employee?.name || '';
              const service =
                firstBooking.service?.name ||
                firstBooking.serviceName ||
                firstBooking['service-name'] ||
                firstBooking['item-name'] ||
                firstBooking.itemName ||
                attr.service?.name ||
                attr.serviceName ||
                attr['service-name'] ||
                attr['item-name'] ||
                attr.itemName ||
                '';
              const locId = String(
                firstBooking.location?.id ||
                attr.location?.id ||
                firstBooking['location-id'] ||
                attr['location-id'] ||
                ''
              );
              const rawLocName =
                firstBooking.location?.name ||
                firstBooking['location-name'] ||
                attr.location?.name ||
                attr['location-name'] ||
                '';
              const locName = resolveLocationName(
                locId,
                rawLocName,
                locations,
                settings.locationAliases,
                settings.locationNameMode
              );

              const apptDate = dayjs(dateStr).startOf('day');

              if (apptDate.isSameOrAfter(now)) {
                // Future appointment: pick closest upcoming
                if (!nextApptDate || apptDate.isBefore(dayjs(nextApptDate).startOf('day'))) {
                  nextApptDate = dateStr;
                  nextStaff = staff;
                  nextService = service;
                  nextLocation = locName;
                  nextLocId = locId;
                  daysUntilNext = getDaysUntil(dateStr);
                }
              } else {
                // Past visit: pick most recent
                if (!lastApptDate || apptDate.isAfter(dayjs(lastApptDate).startOf('day'))) {
                  lastApptDate = dateStr;
                  lastStaff = staff;
                  lastService = service;
                  lastLocation = locName;
                  lastLocId = locId;
                  daysAbsent = getDaysAbsent(dateStr);
                }
              }
            }
          }

          // Fallback to /sales if no past visit was found in appointments
          if (!lastApptDate) {
            if (signal.aborted || getCurrentSessionId() !== sessionId) return;
            const salesRes = await api.getCustomerSales(cid, workspaceId).catch(() => null);
            if (signal.aborted || getCurrentSessionId() !== sessionId) return;
            const salesData = salesRes?.data?.data || salesRes?.data || [];
            if (Array.isArray(salesData) && salesData.length > 0) {
              const firstSale = salesData[0];
              const salesIncluded = salesRes?.data?.included || (salesRes as any)?.included || [];
              const saleAttrs = firstSale?.attributes || {};
              const invoiceDate = saleAttrs['invoice-date'] || saleAttrs['created-at'] || '';
              if (invoiceDate) {
                lastApptDate = invoiceDate;
                daysAbsent = getDaysAbsent(invoiceDate);
              }
              const saleLocId = String(saleAttrs['location-id'] || saleAttrs.location?.id || '');
              const rawSaleLocName = saleAttrs['location-name'] || saleAttrs.location?.name || '';
              lastLocation = resolveLocationName(
                saleLocId,
                rawSaleLocName || lastLocation,
                locations,
                settings.locationAliases,
                settings.locationNameMode
              );
              lastLocId = saleLocId || lastLocId;

              const saleEntities = extractRelationalSaleItemEntities(firstSale, salesIncluded);
              if (saleEntities.service) lastService = saleEntities.service;
              if (saleEntities.staff) lastStaff = saleEntities.staff;

              if (saleEntities.bookingId) {
                if (signal.aborted || getCurrentSessionId() !== sessionId) return;
                const bookingRes = await api.getBookingDetail(saleEntities.bookingId, workspaceId).catch(() => null);
                if (signal.aborted || getCurrentSessionId() !== sessionId) return;
                const resolved = extractRelationalBookingEntities(bookingRes, saleEntities.saleItem);
                if (resolved.staff) lastStaff = resolved.staff;
                if (resolved.service) lastService = resolved.service;
                if (resolved.location) lastLocation = resolved.location;
                if (resolved.locationId) lastLocId = resolved.locationId;
                if (resolved.appointmentDate) {
                  lastApptDate = resolved.appointmentDate;
                  daysAbsent = getDaysAbsent(resolved.appointmentDate);
                }
              }
            }
          }

          if (signal.aborted || getCurrentSessionId() !== sessionId) return;

          const item: CustomerStatsCacheItem = {
            appointments: count,
            lastAppointment: lastApptDate || undefined,
            staff: lastStaff || undefined,
            serviceName: lastService || undefined,
            locationName: lastLocation || undefined,
            lastLocationId: lastLocId || undefined,
            nextAppointment: nextApptDate || undefined,
            nextStaff: nextStaff || undefined,
            nextServiceName: nextService || undefined,
            nextLocationName: nextLocation || undefined,
            nextLocationId: nextLocId || undefined,
            daysUntilNext,
            daysAbsent,
            outstandingSales,
            totalSales,
            lastFetch: Date.now(),
          };

          statsBatch.push({ ...item, customerId: cid });
          statsBatchMap[cid] = item;
          chunkUpdates[cid] = {
            totalAppointments: count,
            ...(outstandingSales !== undefined && !isNaN(outstandingSales) && { outstandingAmount: outstandingSales }),
            ...(totalSales !== undefined && !isNaN(totalSales) && { totalSales }),
            ...(lastApptDate && { lastAppointmentDate: lastApptDate }),
            ...(lastStaff && { lastStaffMember: lastStaff }),
            ...(lastLocId && { lastLocationId: lastLocId }),
            ...(lastService && { lastServiceName: lastService }),
            ...(lastLocation && { lastLocationName: lastLocation }),
            ...(daysAbsent !== undefined && { daysAbsent }),
            ...(nextApptDate && { nextAppointmentDate: nextApptDate }),
            ...(nextStaff && { nextStaffMember: nextStaff }),
            ...(nextService && { nextServiceName: nextService }),
            ...(nextLocation && { nextLocationName: nextLocation }),
            ...(nextLocId && { nextLocationId: nextLocId }),
            ...(daysUntilNext !== undefined && { daysUntilNext }),
          };
        } catch {
          // Ignore failure on single customer
        }
      })
    );

    if (signal.aborted || getCurrentSessionId() !== sessionId) return;

    if (Object.keys(statsBatchMap).length > 0) {
      useCrmStore.getState().setStatsCacheBatch(statsBatchMap);
    }

    if (statsBatch.length > 0) {
      try {
        await saveCustomerStatsBatch(statsBatch);
      } catch (err) {
        console.warn('Failed to save customer stats batch to Dexie:', err);
      }
    }

    if (signal.aborted || getCurrentSessionId() !== sessionId) return;

    current += batch.length;
    onProgress(Math.min(current, total), total);

    if (signal.aborted || getCurrentSessionId() !== sessionId) return;

    if (Object.keys(chunkUpdates).length > 0) {
      onBatchUpdate(chunkUpdates);
      try {
        const allClients = useCrmStore.getState().clients;
        const updated = allClients.filter(
          (c) => chunkUpdates[c.id] || chunkUpdates[c.customerId]
        );
        if (updated.length > 0) {
          await saveCrmClients(updated).catch(console.warn);
        }
      } catch (err) {
        console.warn('Failed to update CRM clients in Dexie:', err);
      }
    }

    if (i + batchSize < queueIds.length) {
      if (signal.aborted || getCurrentSessionId() !== sessionId) return;
      await new Promise((r) => setTimeout(r, 40));
      if (signal.aborted || getCurrentSessionId() !== sessionId) return;
    }
  }
}

export function groupCrmClients(
  clients: CrmClient[],
  groupBy: CrmGroupBy,
  settings: Settings
): Record<string, CrmClient[]> {
  if (groupBy === 'none') return { '': clients };

  if (groupBy === 'staff') {
    const groups = new Map<
      string,
      { label: string; list: CrmClient[]; orderKey: number }
    >();
    for (const c of clients) {
      const staff = c.lastStaffMember || 'Sin asignar';
      const label = `Último Profesional: ${staff}`;
      const orderKey = staff === 'Sin asignar' ? 99 : 1;
      if (!groups.has(staff)) groups.set(staff, { label, list: [], orderKey });
      groups.get(staff)!.list.push(c);
    }
    const sorted = [...groups.entries()].sort((a, b) => {
      if (a[1].orderKey !== b[1].orderKey) return a[1].orderKey - b[1].orderKey;
      return a[1].label.localeCompare(b[1].label);
    });
    const result: Record<string, CrmClient[]> = {};
    for (const [, grp] of sorted) result[grp.label] = grp.list;
    return result;
  }

  if (groupBy === 'location') {
    const groups = new Map<
      string,
      { label: string; list: CrmClient[]; orderKey: number }
    >();
    for (const c of clients) {
      const loc = c.lastLocationName || c.lastLocationId || 'Sede Principal';
      const label = `Sede: ${loc}`;
      const orderKey = loc === 'Sede Principal' ? 99 : 1;
      if (!groups.has(loc)) groups.set(loc, { label, list: [], orderKey });
      groups.get(loc)!.list.push(c);
    }
    const sorted = [...groups.entries()].sort((a, b) => {
      if (a[1].orderKey !== b[1].orderKey) return a[1].orderKey - b[1].orderKey;
      return a[1].label.localeCompare(b[1].label);
    });
    const result: Record<string, CrmClient[]> = {};
    for (const [, grp] of sorted) result[grp.label] = grp.list;
    return result;
  }

  if (groupBy === 'sales_range') {
    const sym = getCurrencySymbol(settings?.currencyCode || 'EUR');
    const vipSales = Math.max(50, Math.round(settings?.vipMinSales || 250));
    const tierUltra = vipSales * 2;
    const tierVip = vipSales;
    const tierMid = Math.round(vipSales / 2.5);

    const groups = new Map<
      string,
      { label: string; list: CrmClient[]; orderKey: number }
    >();
    for (const c of clients) {
      const sales = Number(c.totalSales || 0);
      let key = '';
      let label = '';
      let orderKey = 0;

      if (sales >= tierUltra) {
        key = 'ultra_vip';
        label = `Consumo VIP Elite: Más de ${sym}${tierUltra}.00`;
        orderKey = 1;
      } else if (sales >= tierVip) {
        key = 'vip';
        label = `Consumo VIP: ${sym}${tierVip}.00 - ${sym}${tierUltra}.00`;
        orderKey = 2;
      } else if (sales >= tierMid) {
        key = 'mid';
        label = `Consumo Frecuente: ${sym}${tierMid}.00 - ${sym}${tierVip}.00`;
        orderKey = 3;
      } else {
        key = 'low';
        label = `Consumo Inicial: Menos de ${sym}${tierMid}.00`;
        orderKey = 4;
      }

      if (!groups.has(key)) groups.set(key, { label, list: [], orderKey });
      groups.get(key)!.list.push(c);
    }

    const sorted = [...groups.entries()].sort((a, b) => a[1].orderKey - b[1].orderKey);
    const result: Record<string, CrmClient[]> = {};
    for (const [, grp] of sorted) result[grp.label] = grp.list;
    return result;
  }

  if (groupBy === 'visits_range') {
    const vipAppts = Math.max(4, Math.round(settings?.vipMinAppointments || 10));
    const tierSuper = vipAppts * 2;
    const tierVip = vipAppts;
    const tierMid = Math.max(2, Math.round(vipAppts / 2));

    const groups = new Map<
      string,
      { label: string; list: CrmClient[]; orderKey: number }
    >();
    for (const c of clients) {
      const visits = Number(c.totalAppointments || 0);
      let key = '';
      let label = '';
      let orderKey = 0;

      if (visits >= tierSuper) {
        key = 'v_super';
        label = `Frecuencia Alta: Más de ${tierSuper} visitas`;
        orderKey = 1;
      } else if (visits >= tierVip) {
        key = 'v_vip';
        label = `Frecuencia VIP: ${tierVip} a ${tierSuper} visitas`;
        orderKey = 2;
      } else if (visits >= tierMid) {
        key = 'v_mid';
        label = `Frecuencia Media: ${tierMid} a ${tierVip - 1} visitas`;
        orderKey = 3;
      } else {
        key = 'v_low';
        label = `Frecuencia Inicial: Menos de ${tierMid} visitas`;
        orderKey = 4;
      }

      if (!groups.has(key)) groups.set(key, { label, list: [], orderKey });
      groups.get(key)!.list.push(c);
    }

    const sorted = [...groups.entries()].sort((a, b) => a[1].orderKey - b[1].orderKey);
    const result: Record<string, CrmClient[]> = {};
    for (const [, grp] of sorted) result[grp.label] = grp.list;
    return result;
  }

  if (groupBy === 'debt_status') {
    const minDebt = settings?.debtorMinAmount || 1;
    const groups = new Map<
      string,
      { label: string; list: CrmClient[]; orderKey: number }
    >();
    for (const c of clients) {
      const debt = Number(c.outstandingAmount || 0);
      const withDebt = debt >= minDebt;
      const key = withDebt ? 'with_debt' : 'no_debt';
      const label = withDebt
        ? 'Estado de Deuda: Con saldo deudor'
        : 'Estado de Deuda: Al día (Sin deuda)';
      const orderKey = withDebt ? 1 : 2;

      if (!groups.has(key)) groups.set(key, { label, list: [], orderKey });
      groups.get(key)!.list.push(c);
    }

    const sorted = [...groups.entries()].sort((a, b) => a[1].orderKey - b[1].orderKey);
    const result: Record<string, CrmClient[]> = {};
    for (const [, grp] of sorted) result[grp.label] = grp.list;
    return result;
  }

  return { '': clients };
}
